import { useMemo } from 'react';
import type { Quiz } from '@teachur/http-client';

const DEFAULT_TOTAL_QUESTIONS = 0;

const getCorrectAssessmentIds = (attempts: Quiz[]) => {
  const correctAssessmentIds = new Set<number>();

  attempts.forEach((attempt) => {
    attempt.assessments?.forEach((assessment) => {
      const hasCorrectAnswer = assessment.answers?.some(
        (answer) =>
          answer.correct === true &&
          attempt.answers?.some((userAnswer) => userAnswer.id === answer.id)
      );

      if (hasCorrectAnswer) {
        correctAssessmentIds.add(assessment.id);
      }
    });
  });

  return correctAssessmentIds;
};

const getTotalQuestions = (attempts: Quiz[]) => {
  if (attempts.length === 0) return DEFAULT_TOTAL_QUESTIONS;

  return Math.max(
    ...attempts.map((attempt) => attempt.assessments?.length ?? DEFAULT_TOTAL_QUESTIONS)
  );
};

export const useExamProgress = (attempts: Quiz[]) => {
  return useMemo(() => {
    const submittedAttempts = attempts.filter((attempt) => !attempt.isDraft);

    const totalQuestions = getTotalQuestions(attempts);
    const correctAssessmentIds = getCorrectAssessmentIds(submittedAttempts);

    const correctAnswers = correctAssessmentIds.size;
    const remainingQuestions = Math.max(totalQuestions - correctAnswers, 0);
    const isCompleted = totalQuestions > 0 && correctAnswers === totalQuestions;

    const attemptProgressItems = attempts.map((attempt) => {
      if (attempt.isDraft) {
        const previousSubmittedAttempts = submittedAttempts.filter(
          (submittedAttempt) => new Date(submittedAttempt.createdAt) < new Date(attempt.createdAt)
        );

        const previousCorrectCount = getCorrectAssessmentIds(previousSubmittedAttempts).size;

        return {
          attempt,
          previousCorrectCount,
          currentCorrectCount: previousCorrectCount,
        };
      }

      const submittedAttemptIndex = submittedAttempts.findIndex(
        (submittedAttempt) => submittedAttempt.id === attempt.id
      );

      const previousSubmittedAttempts = submittedAttempts.slice(0, submittedAttemptIndex);
      const currentSubmittedAttempts = submittedAttempts.slice(0, submittedAttemptIndex + 1);

      return {
        attempt,
        previousCorrectCount: getCorrectAssessmentIds(previousSubmittedAttempts).size,
        currentCorrectCount: getCorrectAssessmentIds(currentSubmittedAttempts).size,
      };
    });

    return {
      totalQuestions,
      correctAnswers,
      remainingQuestions,
      isCompleted,
      attemptProgressItems,
    };
  }, [attempts]);
};
